import { useState, useEffect, useRef, Suspense } from "react";
import { 
  Box, 
  Typography, 
  Stack, 
  Snackbar, 
  Alert 
} from "@mui/material";
import { Save, Refresh } from "@mui/icons-material";
import useAuthCheck from "hooks/useAuthCheck";
import { LAYOUT_HEIGHT } from 'state/constants';
import ScrollToTop from 'components/ScrollToTop';
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import StyledButton from 'components/StyledButton';
import { KanbanProvider, useKanban } from "./kanban-provider";
import KanbanBoard from "./kanban-board";
import BoardSkeleton from "./board-skeleton";
import KanbanCommandPalette from "./kanban-command-palette";


function CommandPalette() {
  const {
    saveData,
    loadData,
    openCreateTask,
    openCreateColumn,
  } = useKanban();

  return (
    <Stack direction="row" spacing={1}>
      <StyledButton onClick={openCreateTask}>
        + Task
      </StyledButton>

      <StyledButton onClick={openCreateColumn}>
        + Column
      </StyledButton>

      <StyledButton onClick={saveData}>
        <Save />
      </StyledButton>

      <StyledButton onClick={loadData}>
        <Refresh />
      </StyledButton>
    </Stack>
  );
}


export default function MainPage() {
  useAuthCheck();
  const scrollRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);


  return (
    <KanbanProvider>
      <Box className="main-container-box">
        <FlexBetween>
          <Header title="Kanban Board" subtitle={"List of Tasks"} />
          <Stack 
            direction="row" 
            alignItems="center" 
            gap={1}
          >
            <Typography>⌘K / Ctrl+K</Typography>
            <CommandPalette />
          </Stack>
        </FlexBetween>
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}
          sx={{ gridColumn: "span 12", overflowY: "auto" }}
        >
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              md: "repeat(12, minmax(0, 1fr))",
            }}
            gap={2}
          >

            <Suspense fallback={<BoardSkeleton />}>
              <KanbanBoard />
            </Suspense>

            <KanbanCommandPalette
              open={paletteOpen}
              onClose={() => setPaletteOpen(false)}
            />

            <ScrollToTop scrollRef={scrollRef} />
            <Snackbar
              open={snackbar.open}
              autoHideDuration={3000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Box>          
        </Box>
      </Box>
    </KanbanProvider>
  );
}